// The reader: one section of a work, set for reading: Source Serif in a ~65-character column,
// Scripture references opening their passage pages, footnotes beside their markers, previous and next.
// On the API the text is read from the work's .bslib package with ranged reads (SQLite in a worker,
// src/reader/); paid works open only for readers who own them. On the prototype (and offline, for the
// cleared works) it comes from the static pages in public/library/text.
//
// Select words to highlight them or write a note (synced, src/sync/), or share them with friends as a
// highlight: the words you chose as the quote, the work and section as its source.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, LinkButton } from "../../../components/Button";
import { Sheet } from "../../../components/Dialog";
import { Empty } from "../../../components/Empty";
import { Icon } from "../../../components/Icon";
import sheet from "../../../components/Sheet.module.css";
import { SyncIndicator } from "../../../components/SyncIndicator";
import ui from "../../../components/ui.module.css";
import { ACCESS_LINE, accessState, UNAVAILABLE_NOTE, useEntitlements, useLibrarySource } from "../../../data/catalogueSource";
import { useCreatePost, usePurchasesOpen } from "../../../data/queries";
import { LIMITS } from "../../../data/types";
import { loadStaticPage, useAuthors, useToc, useWork, type Author, type Note, type Page, type Part, type Toc, type Work } from "../../../data/works";
import { useTitle } from "../../../lib/useTitle";
import { Blocks, type Mark } from "../../../reader/Blocks";
import { bookPage } from "../../../reader/packages";
import { packageProblem } from "../../../reader/problems";
import { BookmarkButton, ReaderBar, Ribbon, useBookmark } from "../../../reader/ReaderBar";
import { MarksList, ReaderTools, type Selected } from "../../../reader/ReaderTools";
import { PackageError } from "../../../reader/sqlite/client";
import { usePageTurns } from "../../../reader/usePageTurns";
import { marksFor, useMarks, useRememberPlace } from "../../../reader/useMarks";
import { bookSelectionSubject, datesOf } from "../../../share/subjects";
import s from "./reader.module.css";

export const Route = createFileRoute("/_app/books/$id_/$section")({
  validateSearch: (q: Record<string, unknown>): { plan?: string; day?: number } => ({
    ...(typeof q.plan === "string" && q.plan.length < 60 ? { plan: q.plan } : {}),
    ...(typeof q.day === "number" && q.day > 0 ? { day: q.day } : {}),
  }),
  component: Reader,
});

/** A reader page's text: from the package on the API; from the static pages on the prototype, and as a fallback offline. */
function usePageText(work: Work | null | undefined, toc: Toc | undefined, n: number, readable: boolean) {
  const source = useLibrarySource();
  const pkg = work && source.live ? source.packageOf(work.entry) : null;
  return useQuery({
    queryKey: ["reader", "page", work?.id ?? "", pkg?.key ?? "static", n],
    queryFn: async (): Promise<Page> => {
      if (pkg) {
        try {
          const page = await bookPage(pkg, work!.id, toc!, n);
          if (page) return page;
        } catch (e) {
          // Offline: the cleared works' text is also built into the site.
          if (!(e instanceof PackageError && (e.type === "network" || e.status === 0))) throw e;
        }
      }
      return loadStaticPage(work!.id, n);
    },
    enabled: !!work && !!toc && n >= 0 && readable,
    staleTime: Infinity,
    retry: false,
  });
}

function Reader() {
  const { id, section } = Route.useParams();
  const { data: work, isPending } = useWork(id);
  const { data: toc } = useToc(work ? id : null);
  const { data: ents } = useEntitlements();
  const purchases = usePurchasesOpen();
  const source = useLibrarySource();
  const index = toc ? toc.rows.findIndex((r) => r[0] === section) : -1;
  const n = index >= 0 ? toc!.rows[index]![4] : -1;
  const state = work ? accessState(work.entry, ents, { purchases, webText: work.text }) : null;
  // On the API, free works and owned ones open; on the prototype, the cleared works with built text.
  const readable = !!work && (source.live ? state === "free" || state === "owned" : work.text);
  const { data: page, error, refetch } = usePageText(work, toc, n, readable);
  useTitle(page?.title ?? work?.title ?? "Library");

  if (isPending || (work && !toc)) return <div className={ui.page} aria-busy="true" aria-label="Opening the book" />;
  if (!work || index < 0)
    return (
      <div className={ui.page}>
        <Empty title="This section isn’t in the library.">
          <LinkButton to="/library" icon="back">
            Library
          </LinkButton>
        </Empty>
      </div>
    );
  if (!readable)
    return (
      <div className={ui.page}>
        <Empty
          title={
            state === "unavailable"
              ? `${work.title} isn’t available yet.`
              : state === "get" || state === "app"
                ? `${work.title} isn’t in your library yet.`
                : `${work.title} is read in the Bible Standard app.`
          }
        >
          {state === "unavailable" && <p className={s.why}>{UNAVAILABLE_NOTE}</p>}
          {state === "app" && <p className={s.why}>{ACCESS_LINE.app}.</p>}
          <LinkButton to="/books/$id" params={{ id: work.id }}>
            {state === "get" ? ACCESS_LINE.get : "See what’s in it"}
          </LinkButton>
        </Empty>
      </div>
    );
  if (error) {
    const p = packageProblem(error);
    return (
      <div className={ui.page}>
        <Empty title={p.title}>
          {p.detail && <p className={s.why}>{p.detail}</p>}
          <Button onClick={() => void refetch()}>Try again</Button>
        </Empty>
      </div>
    );
  }
  if (!page)
    return (
      <div className={s.page} aria-busy="true" aria-label="Loading the section">
        <div className={s.loading}>
          {Array.from({ length: 8 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
      </div>
    );
  return <Section key={`${id}:${n}`} work={work} toc={toc!} page={page} anchor={section} />;
}

/** The author of a section: a Father's own name from the section facets, else the work's. */
function authorOf(work: Work, uid: string, authors: Author[] | undefined): string {
  const own = authors?.find((a) => !a.collection && a.works.some((w) => w.libraryId === work.id && w.sections?.includes(uid)));
  return own?.name ?? work.author;
}

type Chosen = { text: string; uid: string; title: string };

function Section({ work, toc, page, anchor }: { work: Work; toc: Toc; page: Page; anchor: string }) {
  const { plan, day } = Route.useSearch();
  const { data: authors } = useAuthors();
  const article = useRef<HTMLElement>(null);
  const [sharing, setSharing] = useState<Chosen | null>(null);
  const notes = useMemo(() => new Map(page.parts.flatMap((p) => p.notes.map((x) => [x.id, x] as const))), [page]);
  const prev = toc.pages[page.page - 1];
  const next = toc.pages[page.page + 1];
  const headAuthor = authorOf(work, page.uid, authors);
  const marks = useMarks(work.id);
  const remember = useRememberPlace();
  const uids = new Set(page.parts.map((p) => p.uid));
  const titleOf = (uid?: string) => page.parts.find((p) => p.uid === uid)?.title ?? page.title;
  const bookmark = useBookmark(work.id, { sectionUid: page.uid }, page.title);
  const navigate = useNavigate();
  const turnTo = (at: number | undefined) =>
    at !== undefined && void navigate({ to: "/books/$id/$section", params: { id: work.id, section: toc.rows[at]![0]! }, search: { plan, day } });
  usePageTurns(
    () => turnTo(prev),
    () => turnTo(next),
  );

  // Opened at a section inside the page (a confession's paragraph): bring it into view.
  useEffect(() => {
    if (anchor !== page.uid) document.getElementById(`s-${CSS.escape(anchor)}`)?.scrollIntoView({ block: "start" });
  }, [anchor, page.uid]);

  // Remember the place: the section, and how far through the work it is.
  useEffect(() => {
    void remember(work.id, { sectionUid: page.uid }, toc.pages.length ? page.page / toc.pages.length : null);
  }, [work.id, page.uid, page.page, toc.pages.length, remember]);

  const here = {
    highlights: marks.highlights.filter((h) => h.anchor.sectionUid && uids.has(h.anchor.sectionUid)),
    notes: marks.notes.filter((n) => n.anchor?.sectionUid && uids.has(n.anchor.sectionUid)),
  };
  const items = [
    ...here.highlights.map((h) => ({ id: h.id, kind: "highlight" as const, label: titleOf(h.anchor.sectionUid), color: h.color, text: h.comment, highlight: h })),
    ...here.notes.map((n) => ({ id: n.id, kind: "note" as const, label: titleOf(n.anchor?.sectionUid), text: n.body, note: n })),
  ];

  return (
    <div className={s.page}>
      <a className="skip-link" href="#text">
        Skip to the text
      </a>
      <ReaderBar
        head={work.shortTitle}
        start={
          <>
            <Link to="/books/$id" params={{ id: work.id }} className={ui.back} aria-label={`Contents of ${work.title}`}>
              <Icon name="back" />
            </Link>
            {plan && (
              <Link to="/plans/$journeyId" params={{ journeyId: plan }} className={s.planLink}>
                Back to {day ? `day ${day}` : "your plan"}
              </Link>
            )}
          </>
        }
        end={
          <>
            <BookmarkButton {...bookmark} label={page.title} />
            <SyncIndicator compact />
          </>
        }
      />

      <header className={s.head}>
        <Ribbon kept={bookmark.kept} />
        <h1 className={s.title}>{page.title}</h1>
        <p className={s.from}>
          {/* The parent heading, unless it only repeats the author in capitals (the ANF's "MATHETES"). */}
          {page.parent && page.parent.toLowerCase() !== headAuthor.toLowerCase() && page.parent !== page.parent.toUpperCase() && <>{page.parent}. </>}
          <Link to="/books/$id" params={{ id: work.id }}>
            <cite>{work.title}</cite>
          </Link>
          {headAuthor && headAuthor !== work.title && <>, {headAuthor}</>}
        </p>
      </header>

      <article id="text" tabIndex={-1} className={s.text} ref={article} aria-label={page.title} lang={work.language ?? undefined}>
        {page.parts.map((p, k) => (
          <PartView key={p.uid} part={p} first={k === 0} notes={notes} pageTitle={page.title} marks={marksFor({ sectionUid: p.uid }, here.highlights, here.notes)} />
        ))}
      </article>

      <nav className={s.pager} aria-label="Sections">
        {prev !== undefined ? (
          <Link to="/books/$id/$section" params={{ id: work.id, section: toc.rows[prev]![0]! }} search={{ plan, day }} className={s.prev}>
            <span className={s.pagerLabel}>
              <Icon name="back" size={16} />
              Previous
            </span>
            <span className={s.pagerTitle}>{toc.rows[prev]![1]}</span>
          </Link>
        ) : (
          <span />
        )}
        {next !== undefined && (
          <Link to="/books/$id/$section" params={{ id: work.id, section: toc.rows[next]![0]! }} search={{ plan, day }} className={s.next}>
            <span className={s.pagerLabel}>
              Next
              <Icon name="forward" size={16} />
            </span>
            <span className={s.pagerTitle}>{toc.rows[next]![1]}</span>
          </Link>
        )}
      </nav>

      <MarksList title="Your highlights and notes" items={items} />

      <p className={s.licence}>{[page.licence, page.source ? `Source: ${page.source}` : null].filter(Boolean).join(". ")}.</p>

      <ReaderTools
        libraryId={work.id}
        root={article}
        kind="section"
        highlights={here.highlights}
        notes={here.notes}
        onShare={(sel: Selected) => setSharing({ text: sel.text, uid: sel.anchor.sectionUid!, title: sel.unitTitle })}
        quoteCard={(sel: Selected) => {
          const author = authorOf(work, sel.anchor.sectionUid ?? page.uid, authors);
          const record = authors?.find((a) => !a.collection && a.name === author) ?? null;
          return bookSelectionSubject(sel, { author, dates: datesOf(authors, author), title: work.title, inSet: !!work.set, chapterTitle: page.title, portrait: record });
        }}
      />

      {sharing && <ShareSheet work={work} chosen={sharing} author={authorOf(work, sharing.uid, authors)} onClose={() => setSharing(null)} />}
    </div>
  );
}

function PartView({ part, first, notes, pageTitle, marks }: { part: Part; first: boolean; notes: Map<number, Note>; pageTitle: string; marks: Mark[] }) {
  const leadsWithHeading = part.blocks[0]?.t === "h";
  const sameAsTitle = first && leadsWithHeading && part.blocks[0]!.runs?.map((r) => (typeof r === "string" ? r : (r.text ?? ""))).join("").trim() === pageTitle;
  return (
    <section id={`s-${part.uid}`} className={s.part}>
      {!first && !leadsWithHeading && part.title !== pageTitle && <h2 className={s.partTitle}>{part.title}</h2>}
      <div data-unit={part.uid} data-title={part.title}>
        <Blocks blocks={part.blocks} notes={notes} marks={marks} skipFirstHeading={sameAsTitle} />
      </div>
      {part.notes.length > 0 && (
        <ol className={s.notes} aria-label="Notes">
          {part.notes.map((x) => (
            <li key={x.id} id={`fn-${x.id}`}>
              <a href={`#ref-${x.id}`} className={s.noteMark} aria-label={`Back to the text of note ${x.marker}`}>
                {x.marker}
              </a>
              <div>
                <Blocks blocks={x.blocks} notes={notes} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ShareSheet({ work, chosen, author, onClose }: { work: Work; chosen: Chosen; author: string; onClose: () => void }) {
  const [open, setOpen] = useState(true);
  const create = useCreatePost();
  const navigate = useNavigate();
  const [body, setBody] = useState("");
  const tooLong = chosen.text.length > LIMITS.quote;
  const locator = chosen.title.length > 80 ? `${chosen.title.slice(0, 79)}…` : chosen.title;
  const label = `${author} · ${work.title}`.slice(0, 200);
  return (
    <Sheet open={open} onClose={onClose} title="Share a highlight" dismissible>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (tooLong) return;
          create.mutate(
            { kind: "highlight", work: { id: work.id, label, locator }, source: { libraryId: work.id, sectionUid: chosen.uid }, quote: chosen.text, body: body.trim(), visibility: "friends" },
            { onSuccess: (p) => void navigate({ to: "/p/$postId", params: { postId: p.id } }) },
          );
        }}
      >
        <figure className={s.preview}>
          <blockquote>{chosen.text}</blockquote>
          <figcaption>
            {author}, <cite>{work.title}</cite>, {locator}
          </figcaption>
        </figure>
        {tooLong && (
          <p className={ui.error} role="alert">
            That’s too long to quote ({chosen.text.length} of {LIMITS.quote} characters). Choose a shorter passage.
          </p>
        )}
        <label htmlFor="highlight-body" className={ui.label}>
          Your reflection <span className={s.optional}>optional</span>
        </label>
        <textarea id="highlight-body" className={ui.area} rows={3} maxLength={LIMITS.reflection} value={body} onChange={(e) => setBody(e.target.value)} />
        {create.isError && (
          <p className={ui.error} role="alert">
            {create.error.message}
          </p>
        )}
        <p className={sheet.small}>Friends see the passage, where it’s from, and what you wrote.</p>
        <div className={sheet.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" icon="share" busy={create.isPending} disabled={tooLong || create.isPending}>
            Share with friends
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
